import { useEffect, useState, useCallback, useContext } from 'react';
import fileDownload from 'js-file-download';
import { 
    MdRefresh, 
    MdCheckCircle, 
    MdHourglassEmpty,
    MdFilterList,
    MdWarning,
    MdAdd,
    MdInfo,
    MdCancel
} from 'react-icons/md';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    TextField,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Badge,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ListSubheader,
    Alert,
    IconButton,
    Snackbar,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { ExpandMore } from '@mui/icons-material';
import { useForm, Controller } from "react-hook-form";
import apiAdmin from '../../services/api-admin';
import './tpm-dashboard.scss';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import { toTitleCase } from '../../services/utilities';
import { AdminUserContext } from '../../contexts/user-context';


const COLORS = {
    inProgress: '#2196f3',
    success: '#4caf50',
    error: '#f44336',
    warning: '#ff9800',
    primary: '#1976d2'
};

const TpmDashboard = () => {
    const { adminUserProfile } = useContext(AdminUserContext);
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [selectedCounties, setSelectedCounties] = useState([]);
    const [availableCounties, setAvailableCounties] = useState([]);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25,
    });
    const [openNewBatchDialog, setOpenNewBatchDialog] = useState(false);
    const [openSkippedDocsDialog, setOpenSkippedDocsDialog] = useState(false);
    const [skippedDocuments, setSkippedDocuments] = useState([]);
    const [loadingSkippedDocs, setLoadingSkippedDocs] = useState(false);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [enabledCounties, setEnabledCounties] = useState(null);
    const [availableDirectories, setAvailableDirectories] = useState([]);
    const [loadingDirectories, setLoadingDirectories] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [countiesInProgress, setCountiesInProgress] = useState([]);
    const [cancellingBatchIds, setCancellingBatchIds] = useState([]);
    const [openCancelConfirmDialog, setOpenCancelConfirmDialog] = useState(false);
    const [cancelTargetBatch, setCancelTargetBatch] = useState(null);
    const { handleSubmit, control, reset, formState: { errors } } = useForm();
    const [groupBatch, setGroupBatch] = useState(false);

    const formatNumber = (num) => {
        if (!num) return '0';
        return num.toLocaleString();
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '0s';
        if (seconds < 60) return `${seconds.toFixed(1)}s`;
        if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
        return `${(seconds / 3600).toFixed(1)}h`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const formatErrorDetails = (errorDetails) => {
        if (!errorDetails) return '';
        if (typeof errorDetails === 'string') return errorDetails;

        try {
            return JSON.stringify(errorDetails, null, 2);
        } catch {
            return String(errorDetails);
        }
    };

    const getFilterObject = useCallback(() => {
        return {
            startDate: dateRange.startDate || null,
            endDate: dateRange.endDate || null,
            counties: selectedCounties.length > 0 ? selectedCounties : null
        };
    }, [dateRange, selectedCounties]);

    const fetchBatches = async () => {
        setLoading(true);
        try {
            const response = await apiAdmin.post('/tpm-analytics/batches-list', getFilterObject());
            const batchesData = response.data.map((batch) => ({
                id: batch.batchId,
                ...batch
            }));
            
            setBatches(batchesData);
            
            // Track counties with batches in progress
            const inProgressCounties = batchesData
                .filter(batch => batch.status === 'In Progress')
                .map(batch => toTitleCase(batch.county))
                .filter(county => county && county !== 'Unknown');
            setCountiesInProgress([...new Set(inProgressCounties)]);
            
            if (availableCounties.length === 0) {
                const counties = [...new Set(batchesData.map(b => toTitleCase(b.county)))].filter(c => c && c !== 'Unknown');
                setAvailableCounties(counties.sort());
            }
        } catch (err) {
            console.error('Error fetching batches:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBatches();
        fetchEnabledCounties();
        fetchAvailableDirectories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchEnabledCounties = async () => {
        try {
            const response = await apiAdmin.get('/tpm-analytics/enabled-counties');
            const groupedByState = groupBy(orderBy(response.data, ['state', 'stateFullName', 'county']), 'stateFullName');
            setEnabledCounties(groupedByState);
        } catch (err) {
            console.error('Error fetching enabled counties:', err);
        }
    };

    const fetchAvailableDirectories = async () => {
        setLoadingDirectories(true);
        try {
            const response = await apiAdmin.get('/tpm-analytics/get-directories-in-share');
            setAvailableDirectories(response.data);
        } catch (err) {
            console.error('Error fetching available directories:', err);
        } finally {
            setLoadingDirectories(false);
        }
    };

    const handleApplyFilters = () => {
        fetchBatches();
    };

    const handleResetFilters = () => {
        setDateRange({
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0]
        });
        setSelectedCounties([]);
        
        fetchBatches();
    };

    const getStatusBadge = (params) => {
        const status = params.value;
        const statusConfig = {
            'In Progress': { 
                color: COLORS.inProgress, 
                icon: <span className="hourglass-spin"><MdHourglassEmpty size={16} /></span>
            },
            'Completed': { color: COLORS.success, icon: <MdCheckCircle size={16} /> },
            'Cancelled': { color: COLORS.error, icon: <MdCancel size={16} /> }
        };

        const config = statusConfig[status] || { color: '#666', icon: null };

        return (
            <Chip
                icon={config.icon}
                label={status}
                size="small"
                sx={{
                    backgroundColor: config.color,
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-icon': {
                        color: 'white'
                    }
                }}
                onClick={() => handleDownloadBatchFile(params.row.batchId, params.row.county, 'Index')}
                />
            );
    };

    const getSuccessRateBadge = (rate) => {
        let color = COLORS.success;
        if (rate < 70) color = COLORS.error;
        else if (rate < 90) color = COLORS.warning;

        return (
            <Chip
                label={`${rate.toFixed(1)}%`}
                size="small"
                sx={{
                    backgroundColor: color,
                    color: 'white',
                    fontWeight: 600,
                    minWidth: '60px'
                }}
                />
            );
    };

    const handleDownloadBatchFile = async (batchId, county, fileType) => {
        try {
            var state = Object.values(enabledCounties).flat().find(c => c.county.toLowerCase() === county.toLowerCase()).state;
            const response = await apiAdmin.get(`/tpm-analytics/download-batch-file?batchId=${batchId}&state=${state}&county=${encodeURIComponent(county)}&fileType=${fileType}`, {
                responseType: 'blob'
            });
            fileDownload(response.data, county + '_' + fileType + '_batch' + batchId + '.txt');
        } catch (error) {
            console.error(`Error downloading ${fileType.toLowerCase()} file:`, error);
            if (error.response && error.response.status === 404) {
                alert(`${fileType} file not found for this batch.`);
            } else {
                alert(`Failed to download ${fileType.toLowerCase()} file. Please try again.`);
            }
        }
    }

    const handleViewSkippedDocuments = async (batchId) => {
        setSelectedBatchId(batchId);
        setLoadingSkippedDocs(true);
        setOpenSkippedDocsDialog(true);
        
        try {
            const response = await apiAdmin.get(`/tpm-analytics/batch-skipped-documents?batchId=${batchId}`);
            setSkippedDocuments(response.data);
        } catch (error) {
            console.error('Error fetching skipped documents:', error);
            alert('Failed to load skipped documents. Please try again.');
            setOpenSkippedDocsDialog(false);
        } finally {
            setLoadingSkippedDocs(false);
        }
    };

    const handleCloseSkippedDocsDialog = () => {
        setOpenSkippedDocsDialog(false);
        setSkippedDocuments([]);
        setSelectedBatchId(null);
    };

    const handleCloseDialog = () => {
        setOpenNewBatchDialog(false);
        reset();
    };

    const onSubmitNewBatch = async (data) => {
        try {
            // Check if county already has a batch in progress
            if (countiesInProgress.includes(data.county)) {
                setSnackbar({
                    open: true,
                    message: `Cannot start a new batch for ${data.county}. A batch is already in progress for this county.`,
                    severity: 'error'
                });
                return;
            }

            const payload = {
                county: data.county,
                state: Object.values(enabledCounties).flat().find(c => c.county === data.county).state,
                folderPath: data.folderPath, 
                startedBy: adminUserProfile.displayName,
                groupBatch: groupBatch
            };

            await apiAdmin.post('/tpm-analytics/start-new-batch', payload);
            
            setSnackbar({
                open: true,
                message: `Batch processing started successfully for ${data.county}!`,
                severity: 'success'
            });
            setGroupBatch(false);
            handleCloseDialog();
            
            // Refresh the batches list to show the new batch
            setTimeout(() => {
                fetchBatches();
            }, 2000);
        } catch (error) {
            console.error('Error starting new batch:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to start batch processing. Please try again.',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const handleOpenCancelConfirm = (batchId, county) => {
        setCancelTargetBatch({ batchId, county });
        setOpenCancelConfirmDialog(true);
    };

    const handleCloseCancelConfirm = () => {
        setOpenCancelConfirmDialog(false);
        setCancelTargetBatch(null);
    };

    const handleConfirmCancelBatch = async () => {
        if (!cancelTargetBatch) return;

        await handleCancelBatch(cancelTargetBatch.batchId, cancelTargetBatch.county);
        handleCloseCancelConfirm();
    };

    const handleCancelBatch = async (batchId, county) => {
        const countyEntry = enabledCounties
            ? Object.values(enabledCounties).flat().find(c => c.county.toLowerCase() === county.toLowerCase())
            : null;

        if (!countyEntry?.state) {
            setSnackbar({
                open: true,
                message: `Unable to determine state for ${toTitleCase(county)}.`,
                severity: 'error'
            });
            return;
        }

        setCancellingBatchIds(prev => [...prev, batchId]);
        try {
            await apiAdmin.post('/tpm-analytics/cancel-running-batch', {
                batchId,
                state: countyEntry.state,
                county: countyEntry.county,
                reason: 'Cancelled from TPM dashboard'
            });

            setSnackbar({
                open: true,
                message: `Cancel request sent for batch #${batchId}.`,
                severity: 'success'
            });

            await fetchBatches();
        } catch (error) {
            console.error('Error cancelling batch:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || `Failed to cancel batch #${batchId}. Please try again.`,
                severity: 'error'
            });
        } finally {
            setCancellingBatchIds(prev => prev.filter(id => id !== batchId));
        }
    };

    const columns = [
        {
            field: 'sessionId',
            headerName: 'Session ID',
            width: 100,
            type: 'number',
            renderCell: (params) => (
                <Box sx={{ fontWeight: 600, color: COLORS.primary }}>
                    #{params.value}
                </Box>
            )
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => getStatusBadge(params)
        },
        {
            field: 'batchId',
            headerName: 'Batch ID',
            width: 100,
            type: 'number',
            renderCell: (params) => (
                params.value === 0 ? '-' :
                <Box sx={{ fontWeight: 600, color: COLORS.primary }}>
                    #{params.value}
                </Box>
            )
        },
        {
            field: 'recordedDate',
            headerName: 'Recorded Date',
            width: 180,
            type: 'dateTime',
            valueGetter: (value) => value ? new Date(value) : null,
            renderCell: (params) => formatDate(params.row.recordedDate)
        },
        {
            field: 'county',
            headerName: 'County',
            width: 150,
            renderCell: (params) => (
                <Box sx={{ fontWeight: 500 }}>
                    {toTitleCase(params.value) || 'Unknown'}
                </Box>
            )
        },
        {
            field: 'startedBy',
            headerName: 'Started By',
            width: 150,
            renderCell: (params) => (
                <Box sx={{ fontWeight: 500 }}>
                    {toTitleCase(params.value) || 'Unknown'}
                </Box>
            )
        },
        {
            field: 'startTime',
            headerName: 'Start Time',
            width: 180,
            type: 'dateTime',
            valueGetter: (value) => value ? new Date(value) : null,
            renderCell: (params) => formatDateTime(params.row.startTime)
        },
        {
            field: 'endTime',
            headerName: 'End Time',
            width: 180,
            type: 'dateTime',
            valueGetter: (value) => value ? new Date(value) : null,
            renderCell: (params) => params.row.status === 'In Progress' ? '-' : formatDateTime(params.row.endTime)
        },
        {
            field: 'totalDocuments',
            headerName: 'Total Docs',
            width: 120,
            type: 'number',
            renderCell: (params) => (
                <Tooltip title={`${params.row.completedDocuments} completed`}>
                    <Box sx={{ fontWeight: 600 }}>
                        {formatNumber(params.value)}
                    </Box>
                </Tooltip>
            )
        },
        {
            field: 'successfulDocs',
            headerName: 'Success',
            width: 120,
            type: 'number',
            renderCell: (params) => (
                <Tooltip title="Click to download index file">
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.7
                            }
                        }}
                        onClick={() => handleDownloadBatchFile(params.row.batchId, params.row.county, 'Index')}
                    >
                        <MdCheckCircle size={16} color={COLORS.success} />
                        <span style={{ fontWeight: 600 }}>{formatNumber(params.value)}</span>
                    </Box>
                </Tooltip>
            )
        },
        {
            field: 'skippedDocs',
            headerName: 'Skipped',
            width: 120,
            type: 'number',
            renderCell: (params) => (
                <Tooltip title={params.value > 0 ? "Click to view skipped documents" : "No skipped documents"}>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            cursor: params.value > 0 ? 'pointer' : 'default',
                            opacity: params.value > 0 ? 1 : 0.5,
                            '&:hover': {
                                opacity: params.value > 0 ? 0.7 : 0.5
                            }
                        }}
                        onClick={() => params.value > 0 && handleViewSkippedDocuments(params.row.batchId)}
                    >
                        {params.value > 0 && <MdWarning size={16} color={COLORS.warning} />}
                        <span style={{ fontWeight: params.value > 0 ? 600 : 400, color: params.value > 0 ? COLORS.warning : 'inherit' }}>
                            {formatNumber(params.value)}
                        </span>
                    </Box>
                </Tooltip>
            )
        },
        {
            field: 'successRate',
            headerName: 'Success Rate',
            width: 130,
            type: 'number',
            renderCell: (params) => {
                if (params.row.status === 'In Progress') {
                    return (
                        <Chip
                            icon={<span className="hourglass-spin"><MdHourglassEmpty size={16} /></span>}
                            label="Pending"
                            size="small"
                            sx={{
                                backgroundColor: '#9e9e9e',
                                color: 'white',
                                fontWeight: 600,
                                minWidth: '60px',
                                '& .MuiChip-icon': {
                                    color: 'white'
                                }
                            }}
                        />
                    );
                }

                if (params.row.status === 'Cancelled') {
                    return (
                        <Chip
                            label={`${params.value.toFixed(1)}%`}
                            size="small"
                            sx={{
                                backgroundColor: '#9e9e9e',
                                color: 'white',
                                fontWeight: 600,
                                minWidth: '60px'
                            }}
                        />
                    );
                }

                return getSuccessRateBadge(params.value);
            }
        },
        {
            field: 'totalTokens',
            headerName: 'Total Tokens',
            width: 140,
            type: 'number',
            renderCell: (params) => (
                <Tooltip title="Total tokens used in batch">
                    <Chip
                        label={formatNumber(params.value)}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                    />
                </Tooltip>
            )
        },
        {
            field: 'avgProcessingTime',
            headerName: 'Avg Time',
            width: 120,
            type: 'number',
            renderCell: (params) => (
                <Box sx={{ fontWeight: 500 }}>
                    {formatDuration(params.value)}
                </Box>
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 110,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const isInProgress = params.row.status === 'In Progress';
                const isCancelling = cancellingBatchIds.includes(params.row.batchId);
                return (
                    <Tooltip title={isInProgress ? 'Cancel batch' : 'Only in-progress batches can be cancelled'}>
                        <span>
                            <IconButton
                                size="small"
                                color="error"
                                disabled={!isInProgress || isCancelling}
                                onClick={() => handleOpenCancelConfirm(params.row.batchId, params.row.county)}
                            >
                                {isCancelling ? <CircularProgress size={18} color="inherit" /> : <MdCancel size={20} />}
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            }
        }
    ];

    const renderFiltersPanel = () => (
        <Accordion className="mb-4" defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center">
                    <MdFilterList size={24} className="me-2" />
                    <Typography variant="h6">Filters</Typography>
                    {(selectedCounties.length > 0) && (
                        <Badge 
                            badgeContent={selectedCounties.length} 
                            color="primary" 
                            className="ms-2"
                        />
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                    <TextField
                        label="Start Date"
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 150 }}
                        size="small"
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 150 }}
                        size="small"
                    />
                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel id="counties-label">Counties</InputLabel>
                        <Select
                            labelId="counties-label"
                            multiple
                            value={selectedCounties}
                            onChange={(e) => setSelectedCounties(e.target.value)}
                            label="Counties"
                            renderValue={(selected) => {
                                if (selected.length === 0) return '';
                                if (selected.length > 2) {
                                    return (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            <Chip 
                                                label={`${selected.length} counties`} 
                                                size="small" 
                                            />
                                        </Box>
                                    );
                                }
                                return (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={value} size="small" />
                                        ))}
                                    </Box>
                                );
                            }}
                        >
                            {availableCounties.map((county) => (
                                <MenuItem key={county} value={county}>{county}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box display="flex" gap={2} alignItems="center">
                        <Button 
                            variant="contained" 
                            onClick={handleApplyFilters}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <MdRefresh />}
                            size="small"
                        >
                            Apply Filters
                        </Button>
                        <Button 
                            variant="outlined" 
                            onClick={handleResetFilters}
                            disabled={loading}
                            size="small"
                        >
                            Reset
                        </Button>
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    );



    const renderCountySelectGroup = (state, counties, groupIndex) => {
        return [
            <ListSubheader key={`header-${groupIndex}`}>{state}</ListSubheader>,
            ...counties.map((c, i) => {
                const countyTitleCase = toTitleCase(c.county);
                const isInProgress = countiesInProgress.includes(countyTitleCase);
                return (
                    <MenuItem 
                        key={`${groupIndex}-${i}`} 
                        value={c.county}
                        disabled={isInProgress}
                        sx={{
                            opacity: isInProgress ? 0.5 : 1,
                            fontStyle: isInProgress ? 'italic' : 'normal'
                        }}
                    >
                        {countyTitleCase}
                        {isInProgress && (
                            <Chip
                                label="In Progress"
                                size="small"
                                sx={{
                                    ml: 1,
                                    backgroundColor: COLORS.inProgress,
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    height: '20px'
                                }}
                            />
                        )}
                    </MenuItem>
                );
            })
        ];
    };

    return (
        <div>
            <title>TPM Dashboard</title>
            <Box p={3} className="tpm-dashboard">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h4">
                        TPM Indexing Dashboard
                    </Typography>
                    <Box display="flex" gap={2}>
                        <Button
                            variant="outlined"
                            startIcon={loading ? <CircularProgress size={20} /> : <MdRefresh />}
                            onClick={fetchBatches}
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<MdAdd />}
                            onClick={() => setOpenNewBatchDialog(true)}
                        >
                            Start New Batch
                        </Button>
                    </Box>
                </Box>

                {renderFiltersPanel()}

                <Card>
                    <CardContent>
                        <Box sx={{ height: 700, width: '100%' }}>
                            <DataGridPro
                                rows={batches}
                                columns={columns}
                                loading={loading}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                pageSizeOptions={[10, 25, 50, 100]}
                                pagination
                                disableRowSelectionOnClick
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            avgProcessingTime: true,
                                        },
                                    },
                                    sorting: {
                                        sortModel: [
                                            { field: 'startTime', sort: 'desc' }
                                        ]
                                    },
                                    pinnedColumns: {
                                        left: ['sessionId', 'status', 'startedBy']
                                    }
                                }}
                                sx={{
                                    '& .MuiDataGrid-cell': {
                                        display: 'flex',
                                        alignItems: 'center'
                                    },
                                    '& .MuiDataGrid-row': {
                                        '&:hover': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                            cursor: 'pointer'
                                        }
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        backgroundColor: '#f5f5f5',
                                    },
                                    '& .MuiDataGrid-columnHeader--pinnedLeft': {
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                                getRowClassName={(params) => 
                                    params.row.status === 'In Progress' ? 'in-progress-row' : ''
                                }
                            />
                        </Box>
                    </CardContent>
                </Card>

                <Box mt={3} textAlign="center" color="text.secondary">
                    <Typography variant="caption">
                        Last updated: {new Date().toLocaleString()} | Total Batches: {batches.length}
                    </Typography>
                </Box>

                <Dialog 
                    open={openNewBatchDialog} 
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Start New Batch</DialogTitle>
                    <form onSubmit={handleSubmit(onSubmitNewBatch)}>
                        <DialogContent>
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Alert severity="info" icon={<MdInfo />}>
                                    If you have just uploaded a directory, it may not be available yet. 
                                    Please wait a few minutes for it to appear in the list.
                                </Alert>

                                {countiesInProgress.length > 0 && (
                                    <Alert severity="warning" icon={<MdWarning />}>
                                        Counties with batches in progress are disabled: {countiesInProgress.join(', ')}
                                    </Alert>
                                )}

                                <Controller
                                    name="county"
                                    control={control}
                                    defaultValue=""
                                    rules={{ required: 'County is required' }}
                                    render={({ field }) => (
                                        <FormControl fullWidth error={!!errors.county}>
                                            <InputLabel id="county-label">County *</InputLabel>
                                            <Select
                                                {...field}
                                                labelId="county-label"
                                                label="County *"
                                            >
                                                {enabledCounties && Object.entries(enabledCounties).map(([state, counties], i) => {
                                                    return renderCountySelectGroup(state, counties, i);
                                                })}
                                            </Select>
                                            {errors.county && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                    {errors.county.message}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    )}
                                />

                                <Box>
                                    <Box display="flex" alignItems="flex-start" gap={1}>
                                        <Controller
                                            name="folderPath"
                                            control={control}
                                            defaultValue=""
                                            rules={{ required: 'Folder path is required' }}
                                            render={({ field }) => (
                                                <FormControl fullWidth error={!!errors.folderPath}>
                                                    <InputLabel id="folder-path-label">Folder Path *</InputLabel>
                                                    <Select
                                                        {...field}
                                                        labelId="folder-path-label"
                                                        label="Folder Path *"
                                                        disabled={loadingDirectories}
                                                    >
                                                        {availableDirectories.map((dir, index) => (
                                                            <MenuItem key={`dir-${index}`} value={dir}>
                                                                {dir}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {errors.folderPath && (
                                                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                            {errors.folderPath.message}
                                                        </Typography>
                                                    )}
                                                    {!loadingDirectories && availableDirectories.length === 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                                            No directories found
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                        <Tooltip title="Refresh directory list">
                                            <IconButton 
                                                onClick={fetchAvailableDirectories}
                                                disabled={loadingDirectories}
                                                color="primary"
                                                sx={{ mt: 1 }}
                                            >
                                                {loadingDirectories ? (
                                                    <CircularProgress size={24} />
                                                ) : (
                                                    <MdRefresh size={24} />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog} color="inherit">
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" color="primary">
                                Start Batch
                            </Button>
                            <FormControlLabel control={<Checkbox checked={groupBatch} onChange={(e) => setGroupBatch(e.target.checked)} />} label="Group Batch"/>
                        </DialogActions>
                    </form>
                </Dialog>

                <Dialog 
                    open={openSkippedDocsDialog} 
                    onClose={handleCloseSkippedDocsDialog}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        Skipped Documents - Batch #{selectedBatchId}
                    </DialogTitle>
                    <DialogContent>
                        {loadingSkippedDocs ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                <CircularProgress />
                            </Box>
                        ) : skippedDocuments.length === 0 ? (
                            <Typography>No skipped documents found for this batch.</Typography>
                        ) : (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Total: {skippedDocuments.length} document{skippedDocuments.length !== 1 ? 's' : ''}
                                </Typography>
                                <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                    {skippedDocuments.map((doc, index) => (
                                        <Card key={doc.instrumentNumber || `doc-${index}`} sx={{ mb: 2 }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                                    {doc.fileName || `Document ${index + 1}`}
                                                </Typography>
                                                {doc.instrumentNumber && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Instrument Number:</strong> {doc.instrumentNumber}
                                                    </Typography>
                                                )}
                                                {doc.instrumentType && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Instrument Type:</strong> {doc.instrumentType}
                                                    </Typography>
                                                )}
                                                {doc.category && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Category:</strong> {doc.category}
                                                    </Typography>
                                                )}
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    <strong>Processing Time:</strong> {formatDateTime(doc.processingStartTime)}
                                                </Typography>
                                                {(doc.errorMessage || doc.errorDetails) && (
                                                    <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                                                        {doc.errorMessage && (
                                                            <Typography variant="body2" color="text.primary">
                                                                <strong>Error:</strong> {doc.errorMessage}
                                                            </Typography>
                                                        )}
                                                        {doc.errorDetails && (
                                                            <Accordion
                                                                disableGutters
                                                                elevation={0}
                                                                sx={{
                                                                    mt: 1,
                                                                    bgcolor: 'transparent',
                                                                    '&:before': {
                                                                        display: 'none'
                                                                    }
                                                                }}
                                                            >
                                                                <AccordionSummary
                                                                    expandIcon={<ExpandMore />}
                                                                    sx={{
                                                                        minHeight: '32px',
                                                                        px: 0,
                                                                        '& .MuiAccordionSummary-content': {
                                                                            my: 0.5
                                                                        }
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" color="text.primary">
                                                                        <strong>Error Details</strong>
                                                                    </Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails sx={{ px: 0, pt: 0 }}>
                                                                    <Box
                                                                        component="pre"
                                                                        sx={{
                                                                            m: 0,
                                                                            p: 1,
                                                                            borderRadius: 1,
                                                                            bgcolor: 'common.white',
                                                                            color: 'text.primary',
                                                                            fontSize: '0.75rem',
                                                                            whiteSpace: 'pre-wrap',
                                                                            wordBreak: 'break-word'
                                                                        }}
                                                                    >
                                                                        {formatErrorDetails(doc.errorDetails)}
                                                                    </Box>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        )}
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseSkippedDocsDialog} color="inherit">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={openCancelConfirmDialog}
                    onClose={handleCloseCancelConfirm}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Cancel Running Batch?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to cancel batch #{cancelTargetBatch?.batchId} for {toTitleCase(cancelTargetBatch?.county)}?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseCancelConfirm} color="inherit">
                            No
                        </Button>
                        <Button
                            onClick={handleConfirmCancelBatch}
                            color="error"
                            variant="contained"
                            disabled={cancelTargetBatch ? cancellingBatchIds.includes(cancelTargetBatch.batchId) : false}
                        >
                            Yes, Cancel
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={handleCloseSnackbar} 
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                        variant="filled"
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </div>
    );
};

export default TpmDashboard;
